<template>
  <div class="flex h-full border-t border-gray-200 dark:border-gray-700">
    
    <div class="w-64 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 flex flex-col shrink-0 ">
      <div class="p-3 border-b bg-gray-50 dark:bg-gray-900 dark:border-gray-800 text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
        Carpetas
      </div>
      
      <div class="flex-1 overflow-y-auto p-2 scrollbar-thin">
        <div 
          @click="setCurrentFolder(null)"
          class="flex items-center gap-2 px-3 py-2 rounded-md cursor-pointer text-sm mb-1 transition-colors"
          :class="currentFolderId === null 
            ? 'bg-blue-50 text-blue-700 dark:bg-gray-700 dark:text-blue-300 font-semibold' 
            : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'"
        >
          <HomeIcon :size="16" /> 
          <span>Inicio</span>
        </div>

        <div 
          v-for="folder in flattenedFolderTree" 
          :key="folder.id"
          class="flex items-center gap-1 cursor-pointer rounded-md py-1.5 text-sm transition-colors mb-0.5 select-none"
          :style="{ paddingLeft: `${(folder.level * 16) + 8}px`, paddingRight: '8px' }"
          :class="currentFolderId === folder.id 
            ? 'bg-blue-50 text-blue-700 dark:bg-gray-700 dark:text-blue-300 font-medium' 
            : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'"
          @click="setCurrentFolder(folder.id)"
        >
           <span v-if="folder.hasChildren" @click.stop="toggleExpand(folder.id)" class="p-0.5 rounded hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-400">
             <ChevronDownIcon v-if="expandedFolders[folder.id]" :size="14" />
             <ChevronRightIcon v-else :size="14" />
           </span>
           <span v-else class="w-4 h-4 inline-block"></span> 

           <FolderIcon :size="16" :class="currentFolderId === folder.id ? 'fill-blue-500 text-blue-500' : 'fill-yellow-100 text-yellow-500'" class="shrink-0"/>
           <span class="truncate ml-1.5">{{ folder.name }}</span>
        </div>
      </div>
    </div>

    <div class="flex-1 flex flex-col min-w-0 bg-slate-50 dark:bg-gray-900 ">
       
       <div class="px-6 py-3 flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 bg-white dark:bg-gray-800 border-b dark:border-gray-700 sticky top-0 z-10">
          <button @click="goUp" :disabled="currentFolderId === null" class="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded disabled:opacity-30 mr-2" title="Subir">
            <ArrowLeftIcon :size="18" />
          </button>
          <span @click="setCurrentFolder(null)" class="cursor-pointer hover:text-blue-600 dark:hover:text-blue-400 flex items-center gap-1">
             <HomeIcon :size="14"/> Inicio
          </span>
          <template v-for="crumb in breadcrumbs" :key="crumb.id">
             <ChevronRightIcon :size="14" class="text-gray-400" />
             <span @click="setCurrentFolder(crumb.id)" class="cursor-pointer hover:text-blue-600 dark:hover:text-blue-400 font-medium text-gray-800 dark:text-gray-200 whitespace-nowrap max-w-[150px] truncate">
               {{ crumb.name }}
             </span>
          </template>
       </div>

       <div class="flex-1 overflow-y-auto p-6">
          <div v-if="loading" class="flex flex-col items-center justify-center py-20 text-gray-400">
             <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-2"></div>
             Cargando...
          </div>

          <div v-else>
              <div v-if="currentSubfolders.length > 0" class="mb-8">
                  <h3 class="text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-3">Carpetas</h3>
                  <div class="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8 gap-4">
                      <div 
                        v-for="folder in currentSubfolders" 
                        :key="folder.id"
                        class="group flex flex-col items-center p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl hover:shadow-md hover:border-blue-300 dark:hover:border-blue-500 cursor-pointer transition-all relative"
                      >
                         <button 
                           @click.stop="deleteFolder(folder)" 
                           class="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity bg-red-500 hover:bg-red-600 text-white rounded p-1.5 shadow-md"
                           title="Borrar carpeta"
                         >
                           <TrashIcon :size="14" />
                         </button>
                         <div @click="setCurrentFolder(folder.id)" class="w-full h-full flex flex-col items-center justify-center">
                             <FolderIcon class="w-12 h-12 text-yellow-400 fill-yellow-100 mb-2 group-hover:scale-110 transition-transform" />
                             <span class="text-sm text-gray-700 dark:text-gray-300 font-medium text-center truncate w-full px-1">{{ folder.name }}</span>
                         </div>
                      </div>
                  </div>
              </div>

              <div>
                  <h3 class="text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                     Archivos <span class="bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300 px-2 py-0.5 rounded-full text-[10px]">{{ files.length }}</span>
                  </h3>
                  
                  <div v-if="files.length === 0 && currentSubfolders.length === 0" class="text-center py-16 border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-xl bg-white/40 dark:bg-gray-800/40">
                      <p class="text-gray-400 text-lg">Esta carpeta está vacía</p>
                  </div>

                  <div class="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8 gap-4">
                      <div 
                        v-for="file in files" 
                        :key="file.id"
                        @click="openFile(file.enlace_digital)"
                        class="group flex flex-col items-center p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl hover:shadow-md hover:border-blue-300 dark:hover:border-blue-500 cursor-pointer transition-all relative"
                      >
                         <div class="mb-3 group-hover:-translate-y-1 transition-transform">
                             <component :is="getFileIcon(file.tipo_doc)" class="w-10 h-10 stroke-1 text-gray-500 dark:text-gray-400" />
                         </div>
                         <span class="text-sm text-gray-700 dark:text-gray-300 font-medium text-center line-clamp-2 w-full px-1 mb-1 leading-tight" :title="file.asunto">
                             {{ file.asunto || "Sin asunto" }}
                         </span>
                         <span class="text-[10px] text-gray-400">{{ formatDate(file.fecha_recepcion) }}</span>
                      </div>
                  </div>
              </div>
          </div>
       </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import axios from 'axios';
import { 
  Folder as FolderIcon, 
  FileText as FileTextIcon, 
  ChevronRight as ChevronRightIcon, 
  ChevronDown as ChevronDownIcon, 
  Home as HomeIcon, 
  ArrowLeft as ArrowLeftIcon,
  File as FileGenericIcon,
  Image as ImageIcon,
  Trash as TrashIcon
} from 'lucide-vue-next';

const API_BASE_URL = process.env.VUE_APP_API_URL || '/api';

// --- EMITS & PROPS ---
const emit = defineEmits(['folder-change']);

// --- ESTADO ---
const folders = ref([]);
const files = ref([]);
const currentFolderId = ref(null);
const expandedFolders = ref({});
const loading = ref(false);

// --- API ---

// 1. Obtener estructura de carpetas (Árbol)
const fetchFolders = async () => {
    try {
        const response = await axios.get(`${API_BASE_URL}/folders/`); 
        // Asumiendo que el endpoint devuelve una lista plana con {id, name, parent}
        folders.value = response.data;
    } catch (error) {
        console.error("Error cargando carpetas:", error);
    }
};

// 2. Obtener archivos de la carpeta actual
const fetchFiles = async (folderId) => {
    loading.value = true;
    files.value = [];
    try {
        if (folderId === null) {
            // Si el folderId es null, no se solicitan archivos
            return;
        }
        const params = { folder: folderId };
        const response = await axios.get(`${API_BASE_URL}/files/`, { params });
        files.value = response.data.results || response.data;
    } catch (error) {
        console.error("Error cargando archivos:", error);
    } finally {
        loading.value = false;
    }
};

// --- CICLO DE VIDA ---
onMounted(() => {
    fetchFolders();
    fetchFiles(null);
});

// Exponer método refresh para el Padre
const refresh = () => {
    fetchFiles(currentFolderId.value);
};
defineExpose({ refresh });

// --- LÓGICA DE ÁRBOL Y NAVEGACIÓN ---

const setCurrentFolder = (id) => {
    console.log("setCurrentFolder llamado con id:", id);
    currentFolderId.value = id;
    if (id) {
        expandedFolders.value[id] = true; // Auto-expandir al seleccionar
    }
    // IMPORTANTE: Avisar al padre para el modal de subida
    emit('folder-change', id);
};

// Observar cambios para recargar archivos al cambiar de carpeta
watch(currentFolderId, (newId) => {
    fetchFiles(newId);
});

const toggleExpand = (id) => {
    expandedFolders.value[id] = !expandedFolders.value[id];
};

const goUp = () => {
    const current = folders.value.find(f => f.id === currentFolderId.value);
    setCurrentFolder(current ? current.parent : null);
};

// --- COMPUTED PROPERTIES ---

// Genera lista plana ordenada con niveles para el Sidebar
const flattenedFolderTree = computed(() => {
    const result = [];
    const traverse = (parentId, level) => {
        // Encontrar hijos directos
        const children = folders.value.filter(f => f.parent === parentId);
        
        children.forEach(child => {
            const hasChildren = folders.value.some(f => f.parent === child.id);
            // Mostrar si es nivel raíz (null) O si el padre visualmente está expandido
            // Nota: Para simplificar, renderizamos todo lo que dependa de parents expandidos
            // Pero en sidebar plano, a veces se prefiere mostrar todo. 
            // Aquí usamos lógica: "Si el padre está expandido, muestro al hijo".
            
            // Verificamos si todos los ancestros están expandidos
            let visible = true;
            if (parentId !== null && !expandedFolders.value[parentId]) {
                // Si el padre directo NO está expandido, el hijo no se ve (en lógica recursiva visual)
                // Pero como es una lista plana computada, es complejo.
                // SIMPLIFICACIÓN: Renderizamos todo el árbol pero usamos v-show o filtramos aquí.
                // Para sidebar estilo VSCode:
            }

            // Para este ejemplo, mostraremos el árbol "si el padre está expandido"
            // Necesitamos verificar recursivamente hacia arriba?
            // Haremos algo más simple: Renderizar solo si el padre está expandido.
            const isParentExpanded = parentId === null || expandedFolders.value[parentId];
            
            if (isParentExpanded) {
                 result.push({ ...child, level, hasChildren });
                 if (hasChildren) {
                     traverse(child.id, level + 1);
                 }
            }
        });
    };
    traverse(null, 0);
    return result;
});

const currentSubfolders = computed(() => {
    return folders.value.filter(f => f.parent === currentFolderId.value);
});

const breadcrumbs = computed(() => {
    const path = [];
    let current = folders.value.find(f => f.id === currentFolderId.value);
    // Evitar bucles infinitos si hay datos corruptos
    let safeGuard = 0;
    while (current && safeGuard < 20) {
        path.unshift(current);
        current = folders.value.find(f => f.id === current.parent);
        safeGuard++;
    }
    return path;
});

// --- UTILS ---
const openFile = (url) => {
    if (url) window.open(url, '_blank');
};

// REVISAR: No se muestran iconos específicos, revisar lógica de detección
const getFileIcon = (mimeType) => {
    if (!mimeType) return FileGenericIcon;
    const type = mimeType.toLowerCase();
    if (type.includes('image') || type.includes('foto') || type.includes('jpg') || type.includes('png')) return ImageIcon;
    if (type.includes('pdf')) return FileTextIcon;
    if (type.includes('factura') || type.includes('albaran')) return FileTextIcon;
    return FileGenericIcon;
};

const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString + 'T00:00:00');
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${year}/${month}/${day}`;
};

const deleteFolder = async (folder) => {
    try {
        // Primero obtener los archivos de esta carpeta
        const filesResponse = await axios.get(`${API_BASE_URL}/files/?folder=${folder.id}`);
        const folderFiles = filesResponse.data.results || filesResponse.data;
        
        // Contar subcarpetas
        const subfolders = folders.value.filter(f => f.parent === folder.id);
        
        let confirmMessage = `¿Está seguro que desea eliminar la carpeta "${folder.name}"?`;
        
        if (folderFiles.length > 0 || subfolders.length > 0) {
            confirmMessage += '\n\nCONTENIDO QUE SE ELIMINARÁ:';
            if (subfolders.length > 0) {
                confirmMessage += `\n- ${subfolders.length} subcarpeta(s)`;
            }
            if (folderFiles.length > 0) {
                confirmMessage += `\n- ${folderFiles.length} archivo(s)`;
            }
            confirmMessage += '\n\nEsta acción es IRREVERSIBLE.';
        } else {
            confirmMessage += '\nEsta acción es irreversible.';
        }
        
        if (!confirm(confirmMessage)) {
            return;
        }

        await axios.delete(`${API_BASE_URL}/folders/${folder.id}/`);
        // Recargar las carpetas después de eliminar
        fetchFolders();
        // Si estábamos dentro de la carpeta eliminada, ir a Inicio
        if (currentFolderId.value === folder.id) {
            setCurrentFolder(null);
        }
    } catch (error) {
        console.error('Error al eliminar carpeta:', error);
        alert('Error al eliminar la carpeta.');
    }
};
</script>

<style scoped>
/* Scrollbar fino para el árbol */
.scrollbar-thin::-webkit-scrollbar {
    width: 4px;
}
.scrollbar-thin::-webkit-scrollbar-track {
    background: transparent;
}
.scrollbar-thin::-webkit-scrollbar-thumb {
    background-color: #e5e7eb;
    border-radius: 20px;
}
</style>